import deepEqual from 'fast-deep-equal';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ViewContext } from 'utils/context';
import { IDisplayOptions } from 'utils/context/ViewContext/types';

import {
  generateDefaultOrderColumns,
  generateDefaultShipmentColumns,
} from './utils';

interface IColumnContext {
  displayOptions?: IDisplayOptions;
  hasColumnsModified: boolean;
  updateColumns: (displayOptions: IDisplayOptions) => void;
}

const ColumnContext = createContext<IColumnContext>({
  displayOptions: { activeColumns: [] },
  hasColumnsModified: false,
  updateColumns: () => null,
});

interface IColumnContextProvider {
  children: React.ReactNode;
}

function ColumnContextProvider({ children }: IColumnContextProvider) {
  const defaultOrderColumns = generateDefaultOrderColumns();
  const defaultShipmentColumns = generateDefaultShipmentColumns();
  const { savedOrders, savedShipments, selectedView, viewType, setWillSearch } =
    useContext(ViewContext);

  const isOrder = viewType === 'order';
  const [orderColumns, setOrderColumns] =
    useState<IDisplayOptions>(defaultOrderColumns);
  const [shipmentColumns, setShipmentColumns] = useState<IDisplayOptions>(
    defaultShipmentColumns,
  );

  const activeBaseOrderColumns = defaultOrderColumns.activeColumns.filter(
    ({ enabled }) => enabled,
  );
  const activeBaseShipmentColumns = defaultShipmentColumns.activeColumns.filter(
    ({ enabled }) => enabled,
  );
  const activeOrderColumns = orderColumns?.activeColumns.filter(
    ({ enabled }) => enabled,
  );
  const activeShipmentColumns = shipmentColumns?.activeColumns.filter(
    ({ enabled }) => enabled,
  );

  const hasOrderColumnsModified = !deepEqual(
    activeBaseOrderColumns,
    activeOrderColumns,
  );
  const hasShipmentColumnsModified = !deepEqual(
    activeBaseShipmentColumns,
    activeShipmentColumns,
  );
  const hasColumnsModified = isOrder
    ? hasOrderColumnsModified
    : hasShipmentColumnsModified;

  const allOrderView = selectedView?.id === 'all-orders';
  const allShipmentView = selectedView?.id === 'all-shipments';
  const allPage = allOrderView || allShipmentView;

  const defaultColumns = allShipmentView ? shipmentColumns : orderColumns;
  const displayOptions = !allPage
    ? selectedView?.displayOptions
    : defaultColumns;

  const update = useCallback(
    async ({ activeColumns: next }: IDisplayOptions) => {
      if (!selectedView) return;
      const { id = '', name, filterCriteria } = selectedView;
      const { updateView } =
        selectedView?.id === 'all-orders' ? savedOrders : savedShipments;

      await updateView(id, {
        id,
        name,
        filterCriteria,
        displayOptions: {
          ...displayOptions,
          activeColumns: next,
        },
      });
    },
    [selectedView, savedOrders, savedShipments, displayOptions],
  );

  const setColumnDefaults = useCallback(() => {
    if (allShipmentView) {
      setShipmentColumns(generateDefaultShipmentColumns());
    } else if (allOrderView) {
      setOrderColumns(generateDefaultOrderColumns());
    }
  }, [allShipmentView, allOrderView]);

  const updateColumns = useCallback(
    (displayOptions: IDisplayOptions) => {
      if (allShipmentView) {
        setShipmentColumns(displayOptions);
      } else if (allOrderView) {
        setOrderColumns(displayOptions);
      } else {
        update(displayOptions);
      }
      setWillSearch();
    },
    [allShipmentView, allOrderView, setWillSearch, update],
  );

  useEffect(() => {
    if (allPage) {
      setColumnDefaults();
    }
  }, [allPage, setColumnDefaults, selectedView?.id]);

  const value = useMemo(() => {
    return {
      displayOptions,
      hasColumnsModified,
      updateColumns,
    };
  }, [displayOptions, hasColumnsModified, updateColumns]);

  return (
    <ColumnContext.Provider value={value}>{children}</ColumnContext.Provider>
  );
}

export { ColumnContext, ColumnContextProvider };
